import { ArrowLeft, PenLine } from "lucide-react";
import { useState } from "react";

import type { MatrixVendor, VendorBasicInfo } from "@/types/product-request";

import { useGetMyActiveRequests } from "@/api/product-request";
import { ChatInterface } from "@/components/product-request/chat-interface";
import { ProductRequestForm } from "@/components/product-request/request-form";
import { RequestList } from "@/components/product-request/request-list";
import { VendorProfile } from "@/components/product-request/vendor-profile";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

export function BuyerProductRequestDialog() {
	const [currentRequestId, setCurrentRequest] = useState<string | null>(null);
	const [selectedVendor, setSelectedVendor] = useState<VendorBasicInfo & MatrixVendor | null>(null);
	const [showChat, setShowChat] = useState(false);
	const [showForm, setShowForm] = useState(false);
	const [open, setOpen] = useState(false);

	const myRequestsQuery = useGetMyActiveRequests();

	// const acceptedVendorsQuery = useGetAcceptedVendors(currentRequestId ?? "");

	const handleRequestCreated = (requestId: string) => {
		setCurrentRequest(requestId);
		setShowForm(false);
	};

	const handleRequestSelect = (requestId: string) => {
		setCurrentRequest(requestId);
		setShowForm(false);
	};

	const handleVendorMessage = (vendor: VendorBasicInfo & MatrixVendor) => {
		setSelectedVendor(vendor);
		setShowChat(true);
	};

	const handleBackToVendors = () => {
		setCurrentRequest(null);
		setShowChat(false);
		setSelectedVendor(null);
	};

	const vendors = myRequestsQuery.data?.data?.data.find(req => req.id === currentRequestId)?.vendorBatches.flatMap(va => va.vendorResponses.map(vr => vr.vendor)) ?? [];

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="outline">
					<PenLine className="size-4 mr-2" />
					New Request
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-[90vw] h-[90vh]">
				<DialogHeader className="sr-only">
					<DialogTitle>Product Request</DialogTitle>
					<DialogDescription>
						Manage your product requests here.
					</DialogDescription>
				</DialogHeader>
				<div className="grid grid-cols-1 md:grid-cols-[350px,1fr] h-full">
					{/* Left Column - Request form or vendor list */}
					<div className="h-full md:border-r">
						<Card className="h-full flex flex-col shadow-none border-none rounded-none">
							{showForm
								? (
										<div className="flex flex-col h-full">
											<div className="border-b p-4 flex items-center justify-between">
												<h2 className="font-semibold">New Request</h2>
												<Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
													<ArrowLeft className="size-4" />
													Back to Requests
												</Button>
											</div>
											<ScrollArea className="flex-1">
												<ProductRequestForm onRequestCreated={handleRequestCreated} />
											</ScrollArea>
										</div>
									)
								: currentRequestId
									? (
											<div className="flex flex-col h-full">
												<div className="border-b p-2 flex items-center justify-between">
													<div className="flex items-center gap-2">
														<Button variant="ghost" size="sm" className="px-0" onClick={handleBackToVendors}>
															<ArrowLeft className="size-4" />
														</Button>
														<h2 className="font-semibold">Available Vendors</h2>
													</div>
													<div className="text-sm text-muted-foreground">
														{`${vendors.length} vendors`}
													</div>
												</div>
												<ScrollArea className="flex-1">
													<div className="p-2 space-y-2">
														{myRequestsQuery.isLoading
															? Array.from({ length: 3 }).map(() => (
																	<VendorProfile
																		key={crypto.randomUUID()}
																		vendor={{
																			id: "loading",
																			fullName: "Loading...",
																			email: "",
																			phoneNumber: null,
																			averageRating: null
																		}}
																		isLoading
																	/>
																))
															: vendors.map(vendor => (
																	<VendorProfile
																		key={vendor.id}
																		vendor={vendor}
																		onMessageClick={() => handleVendorMessage(vendor)}
																		compact
																		selected={selectedVendor?.id === vendor.id}
																	/>
																))}
													</div>
												</ScrollArea>
											</div>
										)
									: (
											<div className="flex flex-col h-full w-full">
												<div className="border-b p-4 mb-2 flex items-center justify-between">
													<h2 className="font-semibold">My Requests</h2>
													<Button onClick={() => setShowForm(true)}>
														<PenLine className="size-4 mr-2" />
														New Request
													</Button>
												</div>
												<ScrollArea className="max-h-[600px]">
													<RequestList onRequestSelect={handleRequestSelect} />
												</ScrollArea>
											</div>
										)}
						</Card>
					</div>

					{/* Right Column - Shows either initial form or chat */}
					<div className="h-full flex flex-col">
						{currentRequestId
							? (
									showChat && selectedVendor
										? (
												<Card className="h-full flex flex-col shadow-none border-none rounded-none">
													<div className="p-4 border-b flex items-center gap-4">
														<Button
															variant="ghost"
															size="sm"
															className="md:hidden"
															onClick={handleBackToVendors}
														>
															<ArrowLeft className="size-4" />
														</Button>
														<VendorProfile vendor={selectedVendor} minimal />
													</div>
													<div className="flex-1">
														<ChatInterface
															vendor={selectedVendor}
															productRequestId={currentRequestId}
															vendorId={selectedVendor.id}
														/>
													</div>
												</Card>
											)
										: (
												<div className="h-full flex items-center justify-center text-center text-muted-foreground">
													<div>
														<p className="mb-2">Select a vendor to start chatting</p>
														<p className="text-sm">
															Your product request has been sent to all available vendors
														</p>
													</div>
												</div>
											)
								)
							: (
									<div className="flex h-full items-center justify-center text-center text-muted-foreground">
										<div>
											<p className="mb-2">No request selected</p>
											<p className="text-sm">Select a request from the list or create a new one</p>
										</div>
									</div>
								)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
